import Vue from "vue"
import { get, cloneDeep, merge } from "lodash"

import { SESSION_TYPES } from "@/constants/coaching"

export const FORM_FIELDS = {
  ID: "id",
  USERNAME: "username",
  PLATFORMS: "platforms",
  REGIONS: "regions",
  SPECIALIZATIONS: "specializations",
  SPECIALIZATION_ITEMS: "specialization_items",
  SPECIALIZATION_ITEM_FOCUSES: "specialization_item_focuses",
  RANK: "achievement",
  RATES: "session_rates",
  FEEDBACK: "game_feedback_enabled",
}

const state = () => ({
  savedFormData: {},
  formData: {},
  formStates: {},
  formErrors: {},
  isSubmitting: false,
  sessionTypeDefaultDurations: {
    [SESSION_TYPES.IN_GAME]: 45,
    [SESSION_TYPES.SPECTATE]: 45,
    [SESSION_TYPES.REPLAY_ANALYSIS]: 45,
    [SESSION_TYPES.ONE_VS_ONE]: 45,
    [SESSION_TYPES.TWO_HOUR_SESSION]: 120,
    [SESSION_TYPES.THREE_HOUR_SESSION]: 180,
    [SESSION_TYPES.TWO_WINS_BUNDLE]: 120,
    [SESSION_TYPES.FIVE_WINS_BUNDLE]: 300,
  },
})

const mutations = {
  SET_FORM_DATA: (state, { gameId, property, value }) => {
    let formData = cloneDeep(state.formData[gameId] || {})
    formData[property] = value
    Vue.set(state.formData, gameId, formData)
  },
  SAVE_FORM_DATA: (state, { gameId, formData }) => {
    Vue.set(state.savedFormData, gameId, formData)
  },
  SET_FORM_DISABLED: (state, { gameId, disabled }) => {
    let formStates = cloneDeep(state.formStates[gameId] || {})
    formStates.formDisabled = disabled
    Vue.set(state.formStates, gameId, formStates)
  },
  SET_FORM_SUBMITTING: (state, { gameId, submitting }) => {
    let formStates = cloneDeep(state.formStates[gameId] || {})
    formStates.formSubmitting = submitting
    Vue.set(state.formStates, gameId, formStates)
  },
  SET_FORM_SUBMIT_SUCCEEDED: (state, { gameId, succeeded }) => {
    let formStates = cloneDeep(state.formStates[gameId] || {})
    formStates.formSucceeded = succeeded
    Vue.set(state.formStates, gameId, formStates)
  },
  SET_FORM_SUBMIT_ERRORED: (state, { gameId, errored }) => {
    let formStates = cloneDeep(state.formStates[gameId] || {})
    formStates.formErrored = errored
    Vue.set(state.formStates, gameId, formStates)
  },
  SET_FORM_ERROR: (state, { gameId, property, error }) => {
    let formErrors = cloneDeep(state.formErrors[gameId] || {})
    formErrors[property] = error
    Vue.set(state.formErrors, gameId, formErrors)
  },
  CLEAR_FORM_ERRORS: (state, gameId) => {
    Vue.set(state.formErrors, gameId, {})
  },
  SET_SESSION_TYPE_SELECTED: (state, { gameId, sessionType, isSelected }) => {
    let formData = cloneDeep(state.formData[gameId] || {})
    formData.selected = merge(formData.selected || {}, {
      [sessionType]: isSelected,
    })
    Vue.set(state.formData, gameId, formData)
  },
}

const actions = {
  setFormData ({ commit }, { gameId, property, value }) {
    commit("SET_FORM_DATA", { gameId, property, value })
  },
  saveFormDataHistory ({ state, commit }, gameId) {
    const formData = cloneDeep(get(state.formData, gameId, {}))
    commit("SAVE_FORM_DATA", { gameId, formData })
  },
  loadFormDataHistory ({ state, commit }, gameId) {
    const formData = get(state.savedFormData, gameId, {})
    for (const property in formData) {
      const value = formData[property]
      commit("SET_FORM_DATA", { gameId, property, value })
    }
  },
  enableForm ({ commit }, gameId) {
    commit("SET_FORM_DISABLED", {
      gameId: gameId,
      disabled: false,
    })
  },
  disableForm ({ commit }, gameId) {
    commit("SET_FORM_DISABLED", {
      gameId: gameId,
      disabled: true,
    })
  },
  setFormSubmitting ({ commit }, { gameId, submitting }) {
    commit("SET_FORM_SUBMITTING", { gameId, submitting })
  },
  setFormSubmitSucceeded ({ commit }, { gameId, succeeded }) {
    commit("SET_FORM_SUBMIT_SUCCEEDED", { gameId, succeeded })
  },
  setFormSubmitErrored ({ commit }, { gameId, errored }) {
    commit("SET_FORM_SUBMIT_ERRORED", { gameId, errored })
  },
  setFormError ({ commit }, { gameId, property, error }) {
    commit("SET_FORM_ERROR", { gameId, property, error })
  },
  clearFormMessages ({ commit }, gameId) {
    commit("CLEAR_FORM_ERRORS", gameId)
  },
  setSessionTypeSelected ({ commit }, { gameId, sessionType, isSelected }) {
    commit("SET_SESSION_TYPE_SELECTED", { gameId, sessionType, isSelected })
  },
  setSessionTypeRateAmount ({ state, commit }, { gameId, sessionType, amount }) {
    const sessionTypeRateData = get(
      state.formData,
      [gameId, FORM_FIELDS.RATES],
      {}
    )

    commit("SET_FORM_DATA", {
      gameId: gameId,
      property: FORM_FIELDS.RATES,
      value: merge(sessionTypeRateData, {
        [sessionType]: {
          cost: {
            amount: Number(amount).toFixed(2),
            currency: "USD",
          },
        },
      }),
    })
  },
  setSessionTypeRateDuration (
    { state, commit, getters },
    { gameId, sessionType, duration }
  ) {
    const sessionTypeRateData = get(
      state.formData,
      [gameId, FORM_FIELDS.RATES],
      {}
    )

    commit("SET_FORM_DATA", {
      gameId: gameId,
      property: FORM_FIELDS.RATES,
      value: merge(sessionTypeRateData, {
        [sessionType]: {
          duration: duration,
        },
      }),
    })
  },
}

const getters = {
  formData: state => state.formData,
  getFormData: state => (gameId, property) =>
    get(state.formData, [gameId, property]),
  isFormEnabled: state => gameId =>
    !get(state.formStates, [gameId, "formDisabled"], true),
  isFormSubmitting: state => gameId =>
    get(state.formStates, [gameId, "formSubmitting"], false),
  isFormSucceeded: state => gameId =>
    get(state.formStates, [gameId, "formSucceeded"], false),
  isFormErrored: state => gameId =>
    get(state.formStates, [gameId, "formErrored"], false),
  getFormError: state => (gameId, property) =>
    get(state.formErrors, [gameId, property]),
  isSessionTypeSelected: state => (gameId, sessionType) =>
    get(state.formData, [gameId, "selected", sessionType], false),
  getSessionTypeDefaultDuration: state => sessionType =>
    get(state.sessionTypeDefaultDurations, sessionType, 45),
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
}



// WEBPACK FOOTER //
// ./src/store/modules/user/coaching/coachGameInformationForm.js